import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDeleteForms } from 'api/forms';
import { ActionsBar, ConfirmationDialogModal } from 'components/shared';
import { DYNAMIC_URLS } from 'consts';
import { TrashIcon, EditIcon, LinkIcon } from 'icons';
import { FormSharingModal } from 'pages/Forms/common';

import { useFormsListContext } from '../context';

export const FormsMassActions = () => {
  const { t } = useTranslation('forms');
  const { massActionsSelectedForms: selectedForms } = useFormsListContext();
  const oneFormSelected = selectedForms.length === 1;
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const { mutateAsync: deleteForms } = useDeleteForms();
  const deleteAction = useCallback(async () => {
    await deleteForms({
      queryParams: { formIds: selectedForms.map(({ id }) => id) },
    });
  }, [deleteForms, selectedForms.map(({ id }) => id)]);
  const navigate = useNavigate();

  const items = useMemo(
    () =>
      [
        {
          name: t('listView.massActions.actionBar.edit'),
          icon: <EditIcon />,
          color: 'blue',
          shouldRender: oneFormSelected,
          action: () => {
            navigate(DYNAMIC_URLS.formEdit({ formId: selectedForms[0].id }));
          },
        },
        {
          name: t('listView.massActions.actionBar.delete'),
          icon: <TrashIcon />,
          color: 'red',
          shouldRender: true,
          action: () => {
            setDeleteConfirmModalOpen(true);
          },
        },
        {
          name: t('listView.massActions.actionBar.sharingDetails'),
          icon: <LinkIcon />,
          shouldRender: oneFormSelected && selectedForms[0].active,
          action: () => {
            setSharingModalOpen(true);
          },
        },
      ].filter(item => item.shouldRender),
    [oneFormSelected],
  );

  if (selectedForms.length === 0) return null;

  return (
    <>
      <ActionsBar
        label={t('common:selected', { number: selectedForms.length })}
        items={items}
      />
      <ConfirmationDialogModal
        title={t('listView.massActions.confirmationDialog.title')}
        message={t('listView.massActions.confirmationDialog.message', {
          count: selectedForms.length,
        })}
        confirmButtonText={t(
          'listView.massActions.confirmationDialog.confirmButton',
        )}
        variant="danger"
        opened={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={deleteAction}
      />
      <FormSharingModal
        form={selectedForms[0]}
        isOpen={sharingModalOpen}
        onClose={() => setSharingModalOpen(false)}
      />
    </>
  );
};
